// src/components/client/ProfileAssistant/servicedistancestep.jsx
import React, { useState, useEffect } from 'react';
import { useProfileStore } from '../../../state/profileStore';

const ServiceDistanceStep = ({ profile, updateProfile, goToNextStep }) => {
  const [distance, setDistance] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [error, setError] = useState('');
  const [isZipLocked, setIsZipLocked] = useState(false);

  // DEBUG: Log the full profile object
  useEffect(() => {
    console.log('FULL PROFILE OBJECT:', JSON.stringify(profile, null, 2));
  }, [profile]);

  // Handle zip code initialization and locking
  useEffect(() => {
    if (profile) {
      // Check for zip code in both possible locations
      const existingZip = profile.b_zip_code || profile.business?.b_zip_code;
      
      if (existingZip) {
        console.log('Found existing zip:', existingZip);
        setZipCode(existingZip);
        setIsZipLocked(true);
      } else {
        console.log('No existing zip found');
        setIsZipLocked(false);
      }
    }
  }, [profile]);

  const handleSubmit = async () => {
    if (!distance || isNaN(distance)) {
      setError('Please enter a valid number.');
      return;
    }

    try {
      const payload = {
        u_id: profile?.u_id,
        b_service_distance: Number(distance),
        u_is_new_user: false,
        businessId: profile?.b_id, // Added to match potential backend expectation
      };

      // Only add zip code to payload if we're explicitly updating it (not locked)
      if (!isZipLocked && zipCode) {
        payload.b_zip_code = zipCode;
      } else if (isZipLocked && !profile?.b_zip_code) {
        payload.b_zip_code = profile?.business?.b_zip_code; // Ensure zip is included if locked
      }

      console.log('Final submission payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(`/api/profile/update-service-distance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // Add any required auth headers if needed (e.g., token from profile)
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('API response:', JSON.stringify(data, null, 2));

      // Update Zustand store (distance only) and PRESERVE current user focus
      const miles = Number(distance);
      const currentUserFocus = profile?.u_customer_focus;

      updateProfile({
        b_service_distance: miles,
        u_is_new_user: false,
        u_onboarding_stage: 'ongoing',
        ...(currentUserFocus !== undefined ? { u_customer_focus: currentUserFocus } : {}),
        business: {
          ...(profile.business || {}),
          b_service_distance: miles,
          ...(payload.b_zip_code ? { b_zip_code: payload.b_zip_code } : {})
        }
      });


      // Removed updateLocal since it's not a function
      // Delay before invoking OngoingProfileAssistant
      setTimeout(() => {
        goToNextStep();
      }, 1000); // 1-second delay
    } catch (err) {
      console.error('Submission error:', err.message);
      setError(`Failed to save: ${err.message}. Please try again or contact support.`);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '400px' }}>
      <div style={{ marginBottom: '20px' }}>
        {`Now, ${profile?.u_first_name || 'there'}, I want you to think about your customers. We would like to know how far your customers are within a specific radius around your place of business.\n\nPlease enter your base customer radius range, in miles.`}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <input
          type="number"
          placeholder="30"
          value={distance}
          onChange={(e) => setDistance(e.target.value)}
          style={{
            width: '60px',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        />
        <span>miles from</span>
        <input
          type="text"
          placeholder="Zip Code"
          value={zipCode}
          onChange={(e) => !isZipLocked && setZipCode(e.target.value)}
          readOnly={isZipLocked}
          style={{
            width: '100px',
            padding: '8px',
            border: `1px solid ${isZipLocked ? '#ccc' : '#ddd'}`,
            borderRadius: '4px',
            backgroundColor: isZipLocked ? '#f5f5f5' : '#fff',
            color: isZipLocked ? '#666' : '#000',
            cursor: isZipLocked ? 'not-allowed' : 'text',
          }}
        />
      </div>

      {error && <div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}

      <button
        onClick={handleSubmit}
        style={{
          width: '100%',
          padding: '10px',
          backgroundColor: '#00aef7',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Save and Continue
      </button>
    </div>
  );
};

export default ServiceDistanceStep;